import React, { useState, useEffect } from 'react';
import {
    McTable,
    McButton,
    McIcon,
    McTag,
    McBadge,
} from '@maersk-global/mds-react-wrapper/components-core';
import { agentService, ToolCallLogResponse, PagedToolCallLogResponse } from '../../services/agentService';
import './ActivityLogsPanel.css';

interface ActivityLogsPanelProps {
    agentId: string;
}

const ActivityLogsPanel: React.FC<ActivityLogsPanelProps> = ({ agentId }) => {
    const [logs, setLogs] = useState<ToolCallLogResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchLogs = async () => {
        setLoading(true);
        setError(null);
        try {
            const data: PagedToolCallLogResponse = await agentService.getLogs(agentId);
            setLogs(data.content);
        } catch (err: any) {
            console.error('Failed to fetch logs', err);
            setError(err.message || 'Failed to fetch logs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (agentId && agentId.includes('-')) {
            fetchLogs();

            // Poll for new logs every 10 seconds while viewing
            const interval = setInterval(() => {
                fetchLogs();
            }, 10000);
            return () => clearInterval(interval);
        } else {
            setLoading(false);
            setLogs([]);
        }
    }, [agentId]);

    const formatTime = (isoString: string) => {
        const date = new Date(isoString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) +
            ' on ' + date.toLocaleDateString();
    };

    if (loading && logs.length === 0) {
        return <div className="activity-logs__loading">Loading activity history...</div>;
    }

    if (!agentId || !agentId.includes('-')) {
        return (
            <div className="activity-logs__empty">
                <McIcon icon="shield" />
                <h3>Local Agent</h3>
                <p>Please publish this agent to start tracking activity logs.</p>
            </div>
        );
    }

    return (
        <div className="activity-logs">
            <div className="activity-logs__header">
                <h3>Execution History</h3>
                <McButton variant="outlined" appearance="neutral" onClick={fetchLogs}>
                    <McIcon icon="rotate" slot="prefix" />
                    Refresh
                </McButton>
            </div>

            {error && <div className="activity-logs__error">{error}</div>}

            {logs.length === 0 && !error ? (
                <div className="activity-logs__empty">
                    <McIcon icon="comment-dots" />
                    <h3>No Activity Yet</h3>
                    <p>When this agent receives messages via its trigger, the logs will appear here.</p>
                </div>
            ) : (
                <div className="activity-logs__table-container">
                    <table className="activity-logs__table">
                        <thead>
                            <tr>
                                <th>Time</th>
                                <th>Channel</th>
                                <th>User</th>
                                <th>Action / Tool</th>
                                <th>Status</th>
                                <th>Duration</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((log) => (
                                <tr key={log.logId}>
                                    <td className="log-time">{formatTime(log.executedAt)}</td>
                                    <td>
                                        <McTag appearance="neutral">
                                            {log.channel.toUpperCase()}
                                        </McTag>
                                    </td>
                                    <td className="log-user">{log.userId}</td>
                                    <td className="log-tool">{log.toolName}</td>
                                     <td>
                                        {(() => {
                                            switch (log.status) {
                                                case 'SUCCESS':
                                                    return <McBadge appearance="success">Success</McBadge>;
                                                case 'GUARDRAIL_BLOCKED':
                                                    return <McBadge appearance="warning">Guardrail Blocked</McBadge>;
                                                case 'MODERATION_BLOCKED':
                                                    return <McBadge appearance="warning">Moderation Blocked</McBadge>;
                                                case 'TOOL_ERROR':
                                                    return <McBadge appearance="error">Tool Error</McBadge>;
                                                default:
                                                    return <McBadge appearance="error">Error</McBadge>;
                                            }
                                        })()}
                                    </td>
                                    <td className="log-duration">{log.durationMs}ms</td>
                                    <td className="log-details">
                                        <div className="log-summary" title={log.status === 'ERROR' ? log.errorMessage : log.outputData}>
                                            {log.status === 'ERROR' ? (
                                                <span className="error-text">{log.errorMessage?.substring(0, 60)}...</span>
                                            ) : (
                                                <span>{log.outputData?.substring(0, 60)}...</span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default ActivityLogsPanel;
